import {
  getNestedSubelementsByType,
  getPersonName,
  getPersonRelationships,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import { i18n } from '@fs/zion-locale'
import { MAKE_NEW_RECORD, NO_RELATION_TYPE } from './relationshipConstants'

// Group and relationship reads shared by the Family Members and Relationships cards.

/**
 * Resolved lazily so it follows a locale change.
 *
 * @returns {string} Localized "No Relation" label.
 */
const defaultNoRelationLabel = () => i18n.t('household-details-card.no-relation', 'No Relation')

/**
 * Split a composite relationship key into base key and order suffix.
 *
 * Relationship keys are expected as: <base>_FIRST or <base>_SECOND. If no underscore is found,
 * returns the original value as `relKey` with an empty `order`.
 *
 * @param {string} relKey Composite relationship key (e.g. 'PARENT_CHILD_FIRST').
 * @returns {{ relKey: string, order: string }} Parsed key parts.
 */
export function parseRelKey(relKey) {
  const lastUnderscoreIndex = relKey?.lastIndexOf('_')
  const relKeyPart = lastUnderscoreIndex !== -1 ? relKey?.substring(0, lastUnderscoreIndex) : relKey
  const order = lastUnderscoreIndex !== -1 ? relKey?.substring(lastUnderscoreIndex + 1) : ''
  return { relKey: relKeyPart, order }
}

/**
 * The other people in a person's group, each with their relationship to them. Members with nothing
 * recorded get a no-relation entry, so every member gets a row.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.person - The focused person object.
 * @param {string|number} params.recordId - The ID of the focused person's record.
 * @param {Array<Object>} params.allRecords - Array of all image records.
 * @param {Array<Object>} params.images - Array of SLS images.
 * @param {string} params.currentPartitionKey - The partition key of the focused person.
 * @param {Function} [params.noRelationLabel] - Resolves the label for a pair with nothing recorded
 *   between them. The two cards word it differently.
 * @returns {{relationships: Array<Object>, record: Object|null}} The group's relationships, and the
 *   record itself — null when the person is in no group at all.
 */
export const getPersonsAndRelationshipsForHousehold = ({
  person,
  recordId,
  allRecords,
  images,
  currentPartitionKey,
  noRelationLabel = defaultNoRelationLabel,
}) => {
  // A person bound for a record that does not exist yet has no group to read.
  if (recordId === MAKE_NEW_RECORD) {
    return { relationships: [], record: { id: recordId, partitionKey: currentPartitionKey } }
  }

  const record = allRecords.find((rec) => rec.id === recordId)

  // No record found means no group, so there are no persons to process.
  if (!record) return { relationships: [], record: null }

  // Existing relationships for the focused person, with gendered labels (Father/Mother instead of
  // Parent) when the other person's sex is known (FSS-14997). Edit-mode matching is relKey-based,
  // so gendered display labels are safe.
  const existingRelationshipsMap = new Map()
  getPersonRelationships(person, images, false).forEach((rel) => {
    existingRelationshipsMap.set(rel.otherPersonId, rel)
  })

  const otherRecordPersons =
    getNestedSubelementsByType(record, 'PERSON')?.filter((recordPerson) => recordPerson?.id !== person?.id) || []

  const relationships = otherRecordPersons.map((recordPerson) => {
    const existingRel = existingRelationshipsMap.get(recordPerson.id)

    // If an existing relationship is found, merge it with the person info...
    if (existingRel) {
      return { ...existingRel, person: recordPerson }
    }

    // ...otherwise nothing is recorded between the two.
    return {
      fieldType: NO_RELATION_TYPE,
      relKey: NO_RELATION_TYPE,
      label: noRelationLabel(),
      value: getPersonName({ person: recordPerson, justOneName: true }),
      id: null,
      otherPersonId: recordPerson.id,
      person: recordPerson,
    }
  })

  return { relationships, record }
}

/**
 * Generates a list of household suggestions from the provided records.
 * Each suggestion represents a household, identified by a primary person (if available) or the first person in the record.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} params.records - Array of SLS records.
 * @param {Object} [params.person] - Optional person object to create a "New Household" suggestion.
 * @returns {Array<{ key: string, primaryText: string }>} An array of household suggestion objects,
 * each containing a unique key and the primary person's name as primaryText.
 */
export const getHouseholdSuggestions = ({ records = [], person }) => {
  const households = new Set()

  // Add the "New Household" option if the person is new or has no recordId
  const newHousehold =
    person && (!person?.recordId || person?.recordId === MAKE_NEW_RECORD)
      ? {
          key: MAKE_NEW_RECORD,
          primaryText: i18n.t('household-details-card.new-household', 'New Household: {personName}', {
            personName: getPersonName({ person, justOneName: true }),
          }),
          partitionKey: person?.partitionKey,
        }
      : null

  if (newHousehold) {
    households.add(newHousehold)
  }

  records.forEach((record) => {
    const recordPersons = getNestedSubelementsByType(record, 'PERSON')
    // Select primary person or first as representative of the household
    const primaryPerson = recordPersons.find((recordPerson) => recordPerson.primary) || recordPersons[0]

    if (primaryPerson) {
      const personName = getPersonName({ person: primaryPerson, justOneName: true })
      households.add({
        key: record.id,
        primaryText: personName,
        record,
        partitionKey: record.partitionKey,
      })
    }
  })

  return Array.from(households)
}

/**
 * Merge related person name suggestions from relationships into an existing autosuggest list.
 *
 * Each relationship contributes a suggestion object of the form: { key, primaryText, group }.
 * New suggestions are only appended if their key does not already exist in `namesAutoSuggest`.
 * The input array is mutated for efficiency and then returned.
 *
 * @param {Object} params Wrapper object.
 * @param {Array<Object>} params.relationships Relationship objects containing `otherPersonId`, `value`, and optional `group`.
 * @param {Array<Object>} params.namesAutoSuggest Existing suggestion entries to enrich.
 * @returns {Array<Object>} The updated (mutated) suggestion array.
 */
export function prepareFamilyMemberNamesAutosuggest({ relationships, namesAutoSuggest }) {
  const newSuggestions =
    relationships?.map((rel) => ({
      key: rel?.otherPersonId,
      primaryText: rel?.value,
      group: rel?.group,
      recordId: rel?.person?.recordId || null,
    })) || []

  newSuggestions.forEach((newSuggestion) => {
    const exists = namesAutoSuggest.some((existing) => existing.key === newSuggestion.key)
    if (!exists) {
      namesAutoSuggest.push(newSuggestion)
    }
  })
  return namesAutoSuggest
}

/**
 * Extracts person IDs from CREATE actions in a list of dispatches.
 *
 * Iterates through the provided dispatches and collects the IDs of any persons being created.
 * This is useful for identifying new persons that need to be added to a review list.
 *
 * @param {Array<Object>} dispatches Array of SLS dispatch objects.
 * @returns {Array<string>} Array of person IDs created by the dispatches.
 */
export function getPersonIdsToReviewFromCreateActions(dispatches) {
  if (!dispatches || !Array.isArray(dispatches)) {
    return []
  }

  return dispatches
    .filter((dispatch) => dispatch.type === 'CREATE_ELEMENT' && dispatch.value?.elementType === 'PERSON')
    .map((dispatch) => dispatch.value?.id)
    .filter(Boolean)
}

/**
 * Helper function to check if any person in the household (main person OR relationships) is marked as primary.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.images - The array of SLS images.
 * @param {Object} params.person - The main person object.
 * @param {Object} params.formData - The form data object.
 * @returns {boolean} True if any person in the household is marked as primary.
 */
export function householdHasAnyPrimaryPerson({ images, person, formData }) {
  // Check main person's SLS primary status
  const slsMainPerson = getNestedOrFloatingElementById(images, person.id)
  if (slsMainPerson?.primary) {
    return true
  }

  // Check each non-deleted relationship
  const activeRelationships = formData.relationships.filter((rel) => !rel.isDeleted && rel.personId)

  return activeRelationships.some((rel) => {
    // Check pending changes first (takes precedence)
    if (rel.secondaryPersonPrimaryStatusChange !== null) {
      return rel.secondaryPersonPrimaryStatusChange === true
    }
    // Check current SLS primary status
    const slsSecondaryPerson = getNestedOrFloatingElementById(images, rel.personId)
    return slsSecondaryPerson?.primary === true
  })
}
